import type { JsonApiError } from './types';

/**
 * Codici applicativi noti del BE (presenti in `error.meta.code` o `error.code`).
 * v1.8.0 ha introdotto la famiglia LICENSE_* e le transizioni subscription/holding.
 */
export type ApiErrorCode =
  | 'LICENSE_NOT_IMPLEMENTED_YET'
  | 'LICENSE_REQUIRED'
  | 'LICENSE_EXCLUDED'
  | 'LICENSE_NOT_AVAILABLE_AS_ADDON'
  | 'MISSING_PREREQUISITE'
  | 'SUBSCRIPTION_ALREADY_ACTIVE'
  | 'INVALID_TRANSITION'
  | 'CYCLE_DETECTED'
  | 'CROSS_TENANT_DENIED_CLAIM'
  | 'CROSS_TENANT_DENIED_NOT_DESCENDANT'
  | 'QUOTA_EXCEEDED'
  | 'USER_NOT_FOUND'
  | 'EMPLOYEE_TENANT_NOT_HOSTED_IN_SITE'
  | 'FLAG_ALREADY_ASSIGNED_TO_OTHER_SITE'
  | 'MAPPING_ALREADY_EXISTS'
  | 'LAST_OWNER_CANNOT_BE_REMOVED'
  | (string & {});

/** Mapping `meta.code` → messaggio user-friendly italiano. */
export const API_ERROR_MESSAGES: Record<string, string> = {
  LICENSE_NOT_IMPLEMENTED_YET: 'Questa funzione è a catalogo ma non ancora implementata. La stiamo costruendo — torna a trovarci.',
  LICENSE_REQUIRED: 'Serve una licenza attiva per usare questa funzione. Apri la sezione Licenze per attivarla.',
  LICENSE_EXCLUDED: 'Questa licenza è mutualmente esclusiva con un\'altra già attiva. Disattiva l\'altra prima di procedere.',
  LICENSE_NOT_AVAILABLE_AS_ADDON: 'Questa licenza è acquistabile solo all\'interno di un piano, non come componente aggiuntivo singolo.',
  MISSING_PREREQUISITE: 'Manca una licenza propedeutica. Verifica le dipendenze prima di attivare questa.',
  SUBSCRIPTION_ALREADY_ACTIVE: 'Esiste già una sottoscrizione attiva. Annulla o aggiorna quella corrente prima di crearne una nuova.',
  INVALID_TRANSITION: 'Cambio di stato non consentito nella sottoscrizione corrente.',
  CYCLE_DETECTED: 'La gerarchia capogruppo creerebbe un ciclo. Scegli un parent diverso.',
  CROSS_TENANT_DENIED_CLAIM: 'Non hai i permessi per operare in modalità cross-tenant. Serve il claim "canManageSubTenants".',
  CROSS_TENANT_DENIED_NOT_DESCENDANT: 'Il tenant selezionato non è un sottotenant del tuo. Puoi operare solo sui tenant discendenti.',
  QUOTA_EXCEEDED: 'Hai raggiunto il limite previsto dalla tua licenza per questa risorsa. Aggiorna il piano per aumentare la quota.',
  USER_NOT_FOUND: 'Utente non trovato. Effettua nuovamente il login.',
  // Site Sharing v1 (BE v1.17.1)
  EMPLOYEE_TENANT_NOT_HOSTED_IN_SITE: 'L\'azienda del dipendente non è ospitata in questa sede. Aggiungi prima un\'associazione (mapping) per quel tenant.',
  FLAG_ALREADY_ASSIGNED_TO_OTHER_SITE: 'Questo flag (legale/amministrativa/fatturazione/principale) è già assegnato a un\'altra sede per questo tenant. Rimuovilo da lì prima di assegnarlo qui.',
  MAPPING_ALREADY_EXISTS: 'Questo tenant è già associato attivamente a questa sede.',
  LAST_OWNER_CANNOT_BE_REMOVED: 'Non puoi rimuovere l\'unico proprietario attivo della sede. Assegna prima un altro owner.',
};

export class JsonApiHttpError extends Error {
  readonly status: number;
  readonly errors: JsonApiError[];

  constructor(status: number, errors: JsonApiError[], message?: string) {
    super(message ?? errors[0]?.title ?? errors[0]?.detail ?? `HTTP ${status}`);
    this.name = 'JsonApiHttpError';
    this.status = status;
    this.errors = errors;
  }

  /** Returns a map of "field.path" -> first matching error.detail, for form binding. */
  toFieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const err of this.errors) {
      const ptr = err.source?.pointer ?? err.source?.parameter;
      if (!ptr) continue;
      const field = ptr.replace(/^\/data\/attributes\//, '').replace(/^\/data\/relationships\//, '').replace(/\//g, '.');
      if (!out[field]) out[field] = err.detail ?? err.title ?? 'Errore di validazione';
    }
    return out;
  }

  /** Restituisce il primo `meta.code` o `code` noto, se presente. */
  getApiCode(): ApiErrorCode | null {
    for (const err of this.errors) {
      const code =
        (err.meta && typeof err.meta.code === 'string' ? (err.meta.code as string) : undefined) ??
        err.code;
      if (code) return code as ApiErrorCode;
    }
    return null;
  }

  /** Messaggio user-facing: prima cerca `meta.code` nella tabella nota, poi fallback. */
  getUserMessage(): string {
    const code = this.getApiCode();
    if (code && API_ERROR_MESSAGES[code]) return API_ERROR_MESSAGES[code];
    return this.errors[0]?.detail ?? this.errors[0]?.title ?? this.message;
  }

  isAuthError(): boolean {
    return this.status === 401;
  }

  isForbiddenError(): boolean {
    return this.status === 403;
  }

  isValidationError(): boolean {
    return this.status === 422 || this.status === 400;
  }

  isRateLimitError(): boolean {
    return this.status === 429;
  }

  /** 423 Locked: account bloccato/bannato o senza tenant assegnabile (login/change-tenant). */
  isLockError(): boolean {
    return this.status === 423;
  }

  /** Dati grezzi del lock (LockEnd/CrossTenantBanned/TenantId), se il BE li ha forniti. */
  getLockInfo(): LockInfo | null {
    for (const err of this.errors) {
      const lock = err.meta?.lock;
      if (lock && typeof lock === 'object') return lock as LockInfo;
    }
    return null;
  }

  isLicenseError(): boolean {
    const c = this.getApiCode();
    return c != null && (c.startsWith('LICENSE_') || c === 'MISSING_PREREQUISITE' || c === 'QUOTA_EXCEEDED');
  }
}

/**
 * Shape piatta del lock restituita da /account/login e /refresh-token/change-tenant
 * con status 423 (BE v1.8.2+). I campi arrivano in PascalCase sul wire.
 */
export interface LockInfo {
  lockEnd: string | null;
  crossTenantBanned: boolean;
  tenantId: string | null;
}

function formatLockEnd(iso: string): string | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  try {
    return new Date(t).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return new Date(t).toLocaleString();
  }
}

function lockMessage(lock: LockInfo): string {
  if (lock.crossTenantBanned) {
    return 'Il tuo accesso è stato sospeso dall\'amministratore. Contatta il supporto per riattivarlo.';
  }
  const until = lock.lockEnd ? formatLockEnd(lock.lockEnd) : null;
  if (until) {
    return `Accesso temporaneamente bloccato fino al ${until}. Riprova più tardi o contatta l'amministratore.`;
  }
  return 'Accesso non disponibile: il tuo account non ha aziende attive associate. Contatta l\'amministratore.';
}

export function parseErrorsFromResponse(status: number, body: unknown): JsonApiHttpError {
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;

    // JSON:API standard: { errors: [{ title, detail, ... }] }
    if (Array.isArray(o.errors)) {
      return new JsonApiHttpError(status, o.errors as JsonApiError[]);
    }

    // ASP.NET Core ProblemDetails: { errors: { field: ["msg1", "msg2"] }, title }
    if (o.errors && typeof o.errors === 'object') {
      const flat: JsonApiError[] = [];
      for (const [field, msgs] of Object.entries(o.errors as Record<string, unknown>)) {
        const arr = Array.isArray(msgs) ? (msgs as string[]) : [String(msgs)];
        for (const m of arr) {
          flat.push({ status: String(status), title: 'Errore di validazione', detail: m, source: { pointer: `/data/attributes/${field}` } });
        }
      }
      if (flat.length > 0) return new JsonApiHttpError(status, flat);
    }

    // Custom controller shape: { error: "messaggio" } o { message: "..." }
    const msg = (typeof o.error === 'string' && o.error) || (typeof o.message === 'string' && o.message) || (typeof o.title === 'string' && o.title);
    if (msg) {
      return new JsonApiHttpError(status, [{ status: String(status), title: msg, detail: msg }]);
    }

    // BE 423 lock shape (login/change-tenant): { LockEnd, CrossTenantBanned, TenantId }.
    // Body senza error/message → sintetizziamo un messaggio leggibile e conserviamo
    // i campi grezzi in meta.lock per eventuale UI dedicata (getLockInfo()).
    if (status === 423 && ('LockEnd' in o || 'CrossTenantBanned' in o || 'lockEnd' in o || 'crossTenantBanned' in o)) {
      const lock: LockInfo = {
        lockEnd: (typeof o.LockEnd === 'string' && o.LockEnd) || (typeof o.lockEnd === 'string' && o.lockEnd) || null,
        crossTenantBanned: o.CrossTenantBanned === true || o.crossTenantBanned === true,
        tenantId: (typeof o.TenantId === 'string' && o.TenantId) || (typeof o.tenantId === 'string' && o.tenantId) || null,
      };
      const detail = lockMessage(lock);
      return new JsonApiHttpError(status, [{ status: String(status), title: 'Accesso bloccato', detail, meta: { lock } }]);
    }
  }
  // Fallback senza body leggibile: per gli status più comuni del BE v1.9 (authz
  // per-dipendente sui moduli verticali, approvazioni idempotenti) un messaggio sensato.
  const FALLBACK_BY_STATUS: Record<number, string> = {
    400: 'Richiesta non valida o già processata.',
    403: 'Non hai i permessi per questa operazione.',
    409: 'Operazione in conflitto con lo stato attuale della risorsa.',
    // BE (post-v1.9): workflow POST /api/user-profiles → 424 = uno dei ruoli passati
    // nell'header `roles` non è risolvibile (GUID inesistente o di un altro tenant).
    424: 'Uno dei ruoli indicati non è valido o non appartiene al tenant selezionato.',
  };
  const fallback = FALLBACK_BY_STATUS[status] ?? `HTTP ${status}`;
  return new JsonApiHttpError(status, [{ status: String(status), title: fallback }]);
}
